import { useState } from 'react';
import { Link } from 'react-router-dom';
import { Building2Icon, CreditCardIcon, FileTextIcon, UserIcon } from 'lucide-react';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardFooter, CardHeader, CardTitle } from '@/components/ui/card';
import {
    Dialog,
    DialogBody,
    DialogTitle,
    ScrollDialogContent,
    ScrollDialogFooter,
    ScrollDialogHeader,
} from '@/components/ui/dialog-scroll';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { InvoicesList } from '@/components/InvoicesList';
import { useFetch } from '@/hooks/useFecth';
import useDialog from '@/hooks/useDialog';
import { payments } from '@/services/helpers';
import { useClub } from '../ClubContext';
import CardUpdateForm from '../components/CardUpdateForm';
import ClubSettingsCardHeader from '../components/ClubSettingsCardHeader';
import { getClubSubscriptionStatusInfo } from '../utils/clubSubscriptionStatus';

const contractStatus = {
    ACTIVE: 'Ativo',
    CONCLUDED: 'Concluído',
    CANCELLED: 'Cancelado',
    EXPIRED: 'Expirado',
    CANCELED: 'Cancelado',
    SIGNED: 'Assinado',
};

function formatDate(date) {
    if (!date) return '—';
    return new Date(date).toLocaleDateString('pt-BR');
}

function statusBadgeVariant(color) {
    if (color === 'success') return 'default';
    if (color === 'error') return 'destructive';
    if (color === 'warning') return 'secondary';
    return 'outline';
}

function InfoRow({ label, children }) {
    return (
        <div>
            <span className="text-muted-foreground">{label}: </span>
            <span className="font-medium">{children}</span>
        </div>
    );
}

function DetailDialog({ open, onOpenChange, title, children }) {
    return (
        <Dialog open={open} onOpenChange={onOpenChange}>
            <ScrollDialogContent className="sm:max-w-lg">
                <ScrollDialogHeader>
                    <DialogTitle>{title}</DialogTitle>
                </ScrollDialogHeader>
                <DialogBody>{children}</DialogBody>
                <ScrollDialogFooter>
                    <Button variant="outline" onClick={() => onOpenChange(false)}>
                        Fechar
                    </Button>
                </ScrollDialogFooter>
            </ScrollDialogContent>
        </Dialog>
    );
}

export default function ClubAccount() {
    const [openPayment, setOpenPayment] = useState(false);
    const [openCardForm, setOpenCardForm] = useState(false);
    const { club: data } = useClub();

    const subscriptionStatus = getClubSubscriptionStatusInfo(data?.Signature);
    const { open: openClub, handleClose: closeClub } = useDialog('#conta-clube');
    const { open: openResponsible, handleClose: closeResponsible } = useDialog('#conta-responsavel');
    const { open: openCompany, handleClose: closeCompany } = useDialog('#conta-empresa');
    const { open: openContract, handleClose: closeContract } = useDialog('#conta-contrato');

    const billingType =
        payments[
            data?.Signature?.billingType?.toLowerCase() || data?.Signature?.paymentData?.billingType?.toLowerCase()
        ] || '—';
    const isCreditCard = data?.Signature?.billingType === 'CREDIT_CARD';

    return (
        <div className="flex flex-col gap-6">
            <Card>
                <CardHeader>
                    <ClubSettingsCardHeader
                        icon={CreditCardIcon}
                        title="Conta"
                        description="Dados cadastrais, contrato e pagamentos do clube."
                    />
                </CardHeader>
            </Card>

            <div className="flex flex-col gap-4">
                <Card>
                    <CardHeader>
                        <ClubSettingsCardHeader
                            icon={Building2Icon}
                            title="Dados do clube"
                            description="Informações gerais e status do clube."
                        />
                    </CardHeader>
                    <CardContent className="space-y-2 text-sm">
                        <InfoRow label="Nome">{data?.name || '—'}</InfoRow>
                        <InfoRow label="Razão social">{data?.businessName || '—'}</InfoRow>
                        <div className="flex items-center gap-2">
                            <span className="text-muted-foreground">Status:</span>
                            <Badge variant={data?.status ? 'default' : 'destructive'}>
                                {data?.status ? 'Ativo' : 'Inativo'}
                            </Badge>
                        </div>
                    </CardContent>
                    <CardFooter>
                        <Button variant="outline" size="sm" render={<Link to="#conta-clube" />}>
                            Ver detalhes
                        </Button>
                    </CardFooter>
                </Card>

                <Card>
                    <CardHeader>
                        <ClubSettingsCardHeader
                            icon={UserIcon}
                            title="Responsável"
                            description="Contato principal do clube."
                        />
                    </CardHeader>
                    <CardContent className="space-y-2 text-sm">
                        <InfoRow label="Nome">{data?.responsibleName || '—'}</InfoRow>
                        <InfoRow label="E-mail">{data?.responsibleEmail || '—'}</InfoRow>
                        <InfoRow label="Telefone">{data?.responsiblePhone || '—'}</InfoRow>
                    </CardContent>
                    <CardFooter>
                        <Button variant="outline" size="sm" render={<Link to="#conta-responsavel" />}>
                            Ver detalhes
                        </Button>
                    </CardFooter>
                </Card>

                <Card>
                    <CardHeader>
                        <ClubSettingsCardHeader
                            icon={Building2Icon}
                            title="Empresa"
                            description="Dados cadastrais e endereço."
                        />
                    </CardHeader>
                    <CardContent className="space-y-2 text-sm">
                        <InfoRow label="CNPJ">{data?.document || '—'}</InfoRow>
                        <InfoRow label="Cidade/UF">
                            {data?.city && data?.state ? `${data.city}/${data.state}` : '—'}
                        </InfoRow>
                    </CardContent>
                    <CardFooter>
                        <Button variant="outline" size="sm" render={<Link to="#conta-empresa" />}>
                            Ver detalhes
                        </Button>
                    </CardFooter>
                </Card>

                <Card>
                    <CardHeader>
                        <ClubSettingsCardHeader
                            icon={FileTextIcon}
                            title="Contrato"
                            description="Plano contratado e configurações vinculadas."
                        />
                    </CardHeader>
                    <CardContent className="space-y-2 text-sm">
                        {data?.Contract ? (
                            <>
                                <div className="flex items-center gap-2">
                                    <span className="text-muted-foreground">Status:</span>
                                    <Badge
                                        variant={
                                            ['ACTIVE', 'CONCLUDED'].includes(data.Contract.status) ? 'default' : 'outline'
                                        }
                                    >
                                        {contractStatus[data.Contract.status] || '—'}
                                    </Badge>
                                </div>
                                <InfoRow label="Plano">{data?.Signature?.planData?.name || '—'}</InfoRow>
                                <InfoRow label="Assinado em">{formatDate(data?.Contract?.signedAt)}</InfoRow>
                            </>
                        ) : (
                            <p className="text-muted-foreground">Nenhum contrato encontrado.</p>
                        )}
                    </CardContent>
                    <CardFooter>
                        {data?.Contract ? (
                            <Button variant="outline" size="sm" render={<Link to="#conta-contrato" />}>
                                Ver detalhes
                            </Button>
                        ) : (
                            <Button variant="outline" size="sm" disabled>
                                Ver detalhes
                            </Button>
                        )}
                    </CardFooter>
                </Card>

                <Card>
                    <CardHeader>
                        <ClubSettingsCardHeader
                            icon={CreditCardIcon}
                            title="Pagamento"
                            description="Assinatura e cobranças do clube."
                        />
                    </CardHeader>
                    <CardContent className="space-y-2 text-sm">
                        <div className="flex items-center gap-2">
                            <span className="text-muted-foreground">Status:</span>
                            <Badge variant={statusBadgeVariant(subscriptionStatus.color)}>
                                {subscriptionStatus.label}
                            </Badge>
                        </div>
                        <InfoRow label="Forma de pagamento">{billingType}</InfoRow>
                        <InfoRow label="Próximo vencimento">{formatDate(data?.Signature?.dueDateCycle)}</InfoRow>
                        {subscriptionStatus.description ? (
                            <Alert variant={subscriptionStatus.color === 'error' ? 'destructive' : 'default'}>
                                <AlertDescription>{subscriptionStatus.description}</AlertDescription>
                            </Alert>
                        ) : null}
                    </CardContent>
                    <CardFooter className="flex flex-wrap gap-2">
                        <Button variant="outline" size="sm" onClick={() => setOpenPayment(true)}>
                            Histórico de pagamentos
                        </Button>
                        {isCreditCard ? (
                            <Button variant="outline" size="sm" onClick={() => setOpenCardForm(true)}>
                                Alterar dados do cartão
                            </Button>
                        ) : null}
                    </CardFooter>
                </Card>
            </div>

            <DetailDialog
                open={openClub}
                onOpenChange={isOpen => !isOpen && closeClub()}
                title="Dados do clube"
            >
                <div className="space-y-3 text-sm">
                    <InfoRow label="Nome">{data?.name || '—'}</InfoRow>
                    <InfoRow label="Razão social">{data?.businessName || '—'}</InfoRow>
                    <InfoRow label="CNPJ">{data?.document || '—'}</InfoRow>
                    <div className="flex items-center gap-2">
                        <span className="text-muted-foreground">Status:</span>
                        <Badge variant={data?.status ? 'default' : 'destructive'}>
                            {data?.status ? 'Ativo' : 'Inativo'}
                        </Badge>
                    </div>
                    <InfoRow label="Categorias">{data?.ClubCategory?.length || 0}</InfoRow>
                    {data?.originId ? <InfoRow label="Mig ID">{data.originId}</InfoRow> : null}
                </div>
            </DetailDialog>

            <DetailDialog
                open={openResponsible}
                onOpenChange={isOpen => !isOpen && closeResponsible()}
                title="Dados do responsável"
            >
                <div className="space-y-3 text-sm">
                    <InfoRow label="Nome">{data?.responsibleName || '—'}</InfoRow>
                    <InfoRow label="E-mail">{data?.responsibleEmail || '—'}</InfoRow>
                    <InfoRow label="Telefone">{data?.responsiblePhone || '—'}</InfoRow>
                    <InfoRow label="Cargo">{data?.responsiblePosition || '—'}</InfoRow>
                </div>
            </DetailDialog>

            <DetailDialog
                open={openCompany}
                onOpenChange={isOpen => !isOpen && closeCompany()}
                title="Informações da empresa"
            >
                <div className="space-y-3 text-sm">
                    <InfoRow label="Razão social">{data?.businessName || '—'}</InfoRow>
                    <InfoRow label="CNPJ">{data?.document || '—'}</InfoRow>
                    <div>
                        <span className="text-muted-foreground">Endereço: </span>
                        <p className="mt-1 font-medium">
                            {data?.street ? `Rua: ${data.street}` : 'Rua: —'}
                            {data?.number ? `, Nº: ${data.number}` : ', Nº: —'}
                            {data?.neighborhood ? `, Bairro: ${data.neighborhood}` : ', Bairro: —'}
                            {data?.complement ? `, ${data.complement}` : ''}
                            {data?.zipCode || data?.zipcode ? `, CEP: ${data?.zipCode || data?.zipcode}` : ', CEP: —'}
                        </p>
                        <p className="font-medium">
                            {data?.city || data?.state
                                ? `${data?.city || ''}${data?.city && data?.state ? '/' : ''}${data?.state || ''}`
                                : '—'}
                        </p>
                    </div>
                </div>
            </DetailDialog>

            <DetailDialog
                open={openContract}
                onOpenChange={isOpen => !isOpen && closeContract()}
                title="Detalhes do contrato"
            >
                {data?.Contract ? (
                    <div className="space-y-4 text-sm">
                        <div className="space-y-3">
                            <p className="font-medium">Contrato</p>
                            <InfoRow label="Status">{contractStatus[data.Contract.status] || '—'}</InfoRow>
                            <InfoRow label="Assinado em">{formatDate(data?.Contract?.signedAt)}</InfoRow>
                            <InfoRow label="Plano">{data?.Signature?.planData?.name || '—'}</InfoRow>
                            <InfoRow label="Criado em">{formatDate(data.Contract.createdAt)}</InfoRow>
                        </div>
                        <div className="space-y-3 border-t pt-4">
                            <p className="font-medium">Configurações</p>
                            <InfoRow label="Domínio">{data?.domain || '—'}</InfoRow>
                            <InfoRow label="Package Name">{data?.packageName || '—'}</InfoRow>
                            <InfoRow label="App Intent">{data?.appIntent || '—'}</InfoRow>
                            <InfoRow label="Android">{data?.androidUrl || '—'}</InfoRow>
                            <InfoRow label="iOS">{data?.iosUrl || '—'}</InfoRow>
                        </div>
                    </div>
                ) : (
                    <Alert>
                        <AlertDescription>Nenhum contrato encontrado para este clube.</AlertDescription>
                    </Alert>
                )}
            </DetailDialog>

            <Dialog open={openCardForm} onOpenChange={setOpenCardForm}>
                <ScrollDialogContent className="sm:max-w-md">
                    <ScrollDialogHeader>
                        <DialogTitle>Alterar dados do cartão</DialogTitle>
                    </ScrollDialogHeader>
                    <DialogBody>
                        <CardUpdateForm onSuccess={() => setOpenCardForm(false)} />
                    </DialogBody>
                    <ScrollDialogFooter>
                        <Button variant="outline" onClick={() => setOpenCardForm(false)}>
                            Fechar
                        </Button>
                        <Button type="submit" form="card-update-form">
                            Salvar alterações
                        </Button>
                    </ScrollDialogFooter>
                </ScrollDialogContent>
            </Dialog>

            <PaymentModal club={data} open={openPayment} onClose={() => setOpenPayment(false)} />
        </div>
    );
}

function PaymentModal({ club, open, onClose }) {
    const { data: invoices, isLoading } = useFetch(club?.id ? `clubs/${club.id}/invoices/asaas` : null);

    const [paymentTab, setPaymentTab] = useState('subscription');
    const orderCharges = invoices?.benefits || [];
    const customCharges = invoices?.customCharges || [];
    const hasCustomProducts = Boolean(invoices?.customCharges?.length);
    const hasBenefits = club?.functions?.includes('benefit');

    return (
        <Dialog open={open} onOpenChange={isOpen => !isOpen && onClose()}>
            <ScrollDialogContent className="sm:max-w-4xl">
                <ScrollDialogHeader>
                    <DialogTitle>Histórico de pagamentos</DialogTitle>
                </ScrollDialogHeader>

                <DialogBody>
                    <Tabs value={paymentTab} onValueChange={setPaymentTab}>
                        <TabsList>
                            <TabsTrigger value="subscription">Assinatura</TabsTrigger>
                            {hasCustomProducts ? <TabsTrigger value="custom">Customizados</TabsTrigger> : null}
                            {hasBenefits ? <TabsTrigger value="benefits">Benefícios</TabsTrigger> : null}
                        </TabsList>

                        <TabsContent value="subscription">
                            <SubscriptionPayment dataPayments={invoices?.subscription} club={club} isLoading={isLoading} />
                        </TabsContent>

                        <TabsContent value="benefits">
                            <BenefitsPayment dataPayments={orderCharges} club={club} isLoading={isLoading} />
                        </TabsContent>

                        <TabsContent value="custom">
                            <CustomPayment dataPayments={customCharges} club={club} isLoading={isLoading} />
                        </TabsContent>
                    </Tabs>
                </DialogBody>

                <ScrollDialogFooter>
                    <Button variant="outline" onClick={onClose}>
                        Fechar
                    </Button>
                </ScrollDialogFooter>
            </ScrollDialogContent>
        </Dialog>
    );
}

function SubscriptionPayment({ dataPayments, club, isLoading }) {
    return (
        <Card>
            <CardHeader>
                <CardTitle className="text-base">Cobranças da Assinatura</CardTitle>
            </CardHeader>
            <CardContent>
                <InvoicesList club={club} data={dataPayments} isLoading={isLoading} checkbox={false} />
            </CardContent>
        </Card>
    );
}

function CustomPayment({ dataPayments, club, isLoading }) {
    return (
        <Card>
            <CardHeader>
                <CardTitle className="text-base">Cobranças de Customização</CardTitle>
            </CardHeader>
            <CardContent>
                {dataPayments?.length > 0 ? (
                    <InvoicesList
                        club={club}
                        data={dataPayments}
                        isLoading={isLoading}
                        checkbox={false}
                    />
                ) : (
                    <p className="py-4 text-center text-sm text-muted-foreground">Nenhuma cobrança encontrada</p>
                )}
            </CardContent>
        </Card>
    );
}

function BenefitsPayment({ dataPayments, club, isLoading }) {
    return (
        <Card>
            <CardHeader>
                <CardTitle className="text-base">Cobranças de Benefícios</CardTitle>
            </CardHeader>
            <CardContent>
                {dataPayments?.length > 0 ? (
                    <InvoicesList
                        club={club}
                        data={dataPayments}
                        isLoading={isLoading}
                        checkbox={false}
                    />
                ) : (
                    <p className="py-4 text-center text-sm text-muted-foreground">Nenhuma cobrança encontrada</p>
                )}
            </CardContent>
        </Card>
    );
}
